import { Alert, Box, Button, Collapse, Paper, PaperProps, Stack } from '@mui/material';
import ListItemText from '@mui/material/ListItemText';
import { PaymentMethod } from '@stripe/stripe-js';
import useResponsive from '../../hooks/useResponsive';
import { CenteredRow } from '../containers/CenteredRow';
import { SplitRow } from '../containers/SplitRow';
import Iconify from '../iconify';
import PaymentMethodOption, { mapPaymentToIcon } from './payment/PaymentMethodOption';

// ----------------------------------------------------------------------
interface PaymentMethodListProps {
  paymentMethods: PaymentMethod[];
  selectedPaymentMethodId: string | null;
  onPaymentMethodSelected: (paymentMethodId: string) => void;
  defaultPamentMethodId: string | null;
}

export default function PaymentMethodList(props: PaymentMethodListProps) {
  const {
    paymentMethods,
    selectedPaymentMethodId,
    onPaymentMethodSelected,
    defaultPamentMethodId,
  } = props;
  const dense = useResponsive('down', 'sm');
  const filteredPaymentMethods = paymentMethods.filter(
    (paymentMethod) =>
      paymentMethod.type === 'card' ||
      paymentMethod.type === 'us_bank_account' ||
      paymentMethod.type === 'link'
  );
  const sortedAndFilteredPaymentMethods = filteredPaymentMethods.sort((a, b) => {
    return b.created - a.created;
  });

  // ? Debugging used to confirm that the payment methods are sorted by created date from newest to oldest
  // console.log(sortedAndFilteredPaymentMethods.map((paymentMethod) => getPaymentMethodDescription(paymentMethod) + ` ${new Date(paymentMethod.created * 1000).toLocaleString()}`))

  return (
    <Stack spacing={1}>
      {sortedAndFilteredPaymentMethods.map((option) => {
        const { id } = option;
        return (
          <OptionItem
            dense={dense}
            defaultPaymentMethodId={defaultPamentMethodId}
            paymentMethod={option}
            key={id}
            selected={selectedPaymentMethodId === id}
            onPaymentMethodSelected={() => {
              onPaymentMethodSelected(id);
            }}
          />
        );
      })}
    </Stack>
  );
}

// ----------------------------------------------------------------------

type OptionItemProps = PaperProps & {
  paymentMethod: PaymentMethod;
  selected: boolean;
  onPaymentMethodSelected: (paymentMethodId: string) => void;
  defaultPaymentMethodId: string | null;
  dense?: boolean;
};

export function OptionItem({
  selected,
  onPaymentMethodSelected,
  paymentMethod,
  defaultPaymentMethodId,
  dense,
}: OptionItemProps) {
  const { id } = paymentMethod;
  const cardIcon = mapPaymentToIcon(paymentMethod);
  return (
    <>
      <Paper
        variant="outlined"
        component={Button}
        onClick={() => {
          onPaymentMethodSelected(id);
        }}
        key={paymentMethod.id}
        sx={{
          p: 1,
          cursor: 'pointer',
          ...(selected && {
            boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}`,
            borderColor: (theme) => theme.palette.primary.main,
          }),
          ':hover': { borderColor: (theme) => theme.palette.primary.main },
        }}
      >
        <ListItemText
          primary={
            <SplitRow>
              <CenteredRow sx={{ width: '100%' }}>
                <Box
                  sx={{
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    p: 0.5,
                    display: 'flex',
                  }}
                  mr={2}
                >
                  <Iconify width="auto" height={24} icon={cardIcon} />
                </Box>
                <Stack direction="row" flexGrow={1} width="100%">
                  <PaymentMethodOption
                    dense={dense}
                    defaultPayment={defaultPaymentMethodId === paymentMethod.id}
                    paymentMethod={paymentMethod}
                  />
                </Stack>
              </CenteredRow>
            </SplitRow>
          }
          primaryTypographyProps={{ typography: 'subtitle2' }}
        />
      </Paper>
      <Collapse in={selected && paymentMethod.type === 'us_bank_account'}>
        <USBankAccountDisclaimer />
      </Collapse>
    </>
  );
}

function USBankAccountDisclaimer() {
  return (
    <Alert square severity="info">
      Please note this may take 1-2 business days to process.
    </Alert>
  );
}
